'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  validateInternalNetblock,
  validateNetblock,
} from '@terra/shared-utils';
import type {
  DomainDto,
  NetBlockDto,
  Scope,
} from '@terra/shared-contracts-external-network';
import { toasterApi } from '../../toast/ToastProvider';
import { needsCloudHostingWarning } from './cloud-hosting-warning';
import { runInBatches } from './scope-batch';
import { EXTERNAL_SCOPE_PAGE_SIZE } from './scope-constants';
import type {
  ScopeCounts,
  ScopeEntityChange,
  ScopeFilter,
  ScopeSection,
} from './scope-types';
import type { ExternalScopeListQueryState } from './useExternalScopeListQuery';
import type { ExternalScopeManagementContentProps } from './ExternalScopeManagementContent';

export type ExternalScopeAddOperation =
  | 'addSurfaceDomains'
  | 'addSurfaceNetblocks';

export type UseExternalScopeManagementStateParams = {
  surfaceId: string;
  listQuery: ExternalScopeListQueryState;
  netblocksPage: NetBlockDto[];
  isNetblocksPageLoading: boolean;
  domains: DomainDto[];
  domainTreeTotal: number;
  isDomainsLoading: boolean;
  netblockCounts: ScopeCounts;
  domainCounts: ScopeCounts;
  userProvidedDomains: DomainDto[];
  userProvidedNetblocks: NetBlockDto[];
  isConnectorSurface: boolean;
  isScanActive?: boolean;
  isReadOnly?: boolean;
  isScopeUpdatePending: boolean;
  isDomainAddPending: boolean;
  isNetblockAddPending: boolean;
  updateNetblockScope: (change: {
    netblockId: string;
    scope: Scope;
  }) => Promise<unknown>;
  updateDomainScope: (change: {
    domainId: string;
    scope: Scope;
  }) => Promise<unknown>;
  addDomains: (domains: string[]) => Promise<unknown>;
  addNetblocks: (netblocks: string[]) => Promise<unknown>;
  onAddError?: (failure: {
    error: unknown;
    operation: ExternalScopeAddOperation;
  }) => void;
};

type PendingNetblockScope = {
  netblockIds: string[];
  scope: Scope;
};

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

const mergeNetblockPage = ({
  currentNetblocks,
  netblocks,
  netblockSkip,
}: {
  currentNetblocks: NetBlockDto[];
  netblocks: NetBlockDto[];
  netblockSkip: number;
}): NetBlockDto[] => {
  if (netblockSkip === 0) {
    return netblocks;
  }

  const currentIds = new Set(currentNetblocks.map((netblock) => netblock.id));

  return [
    ...currentNetblocks,
    ...netblocks.filter((netblock) => !currentIds.has(netblock.id)),
  ];
};

export function useExternalScopeManagementState({
  surfaceId,
  listQuery,
  netblocksPage,
  isNetblocksPageLoading,
  domains,
  domainTreeTotal,
  isDomainsLoading,
  netblockCounts,
  domainCounts,
  userProvidedDomains,
  userProvidedNetblocks,
  isConnectorSurface,
  isScanActive = false,
  isReadOnly = false,
  isScopeUpdatePending,
  isDomainAddPending,
  isNetblockAddPending,
  updateNetblockScope,
  updateDomainScope,
  addDomains,
  addNetblocks,
  onAddError,
}: UseExternalScopeManagementStateParams): ExternalScopeManagementContentProps {
  const t = useTranslations('designSystem.networkSettings.externalScope');
  const {
    activeSection,
    setActiveSection,
    scopeFilter,
    setScopeFilter,
    netblockSkip,
    setNetblockSkip,
  } = listQuery;
  const [isAddDomainDialogOpen, setIsAddDomainDialogOpen] = useState(false);
  const [isAddNetblockDialogOpen, setIsAddNetblockDialogOpen] = useState(false);
  const [selectedNetblockIds, setSelectedNetblockIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedDomainIds, setSelectedDomainIds] = useState<Set<string>>(
    new Set(),
  );
  const [loadedNetblocks, setLoadedNetblocks] = useState<NetBlockDto[]>([]);
  const [isLoadingMoreNetblocks, setIsLoadingMoreNetblocks] = useState(false);
  const [netblockReachedEnd, setNetblockReachedEnd] = useState(false);
  const [pendingHostingConfirm, setPendingHostingConfirm] =
    useState<PendingNetblockScope | null>(null);
  const existingDomainValues = useMemo(
    () => userProvidedDomains.map((domain) => domain.fqdn),
    [userProvidedDomains],
  );
  const existingNetblockValues = useMemo(
    () => userProvidedNetblocks.map((netblock) => netblock.cidr),
    [userProvidedNetblocks],
  );
  const isMutating =
    isScopeUpdatePending || isDomainAddPending || isNetblockAddPending;
  const isDisabled = isReadOnly || isMutating || isScanActive;
  const scopeCounts =
    activeSection === 'netblocks' ? netblockCounts : domainCounts;
  const hasMoreNetblocks =
    !netblockReachedEnd &&
    loadedNetblocks.length > 0 &&
    loadedNetblocks.length < scopeCounts[scopeFilter];
  const displayedNetblocks =
    netblockSkip === 0 && loadedNetblocks.length === 0
      ? netblocksPage
      : loadedNetblocks;
  const resetNetblockPagination = useCallback(() => {
    setLoadedNetblocks([]);
    setNetblockSkip(0);
    setIsLoadingMoreNetblocks(false);
    setNetblockReachedEnd(false);
  }, [setNetblockSkip]);

  useEffect(() => {
    resetNetblockPagination();
  }, [resetNetblockPagination, surfaceId]);
  useEffect(() => {
    if (isNetblocksPageLoading) {
      return;
    }

    if (netblocksPage.length === 0) {
      setIsLoadingMoreNetblocks(false);
      if (netblockSkip > 0) {
        setNetblockReachedEnd(true);
      }

      return;
    }

    setLoadedNetblocks((currentNetblocks) =>
      mergeNetblockPage({
        currentNetblocks,
        netblocks: netblocksPage,
        netblockSkip,
      }),
    );
    setNetblockReachedEnd(false);
    setIsLoadingMoreNetblocks(false);
  }, [netblocksPage, netblockSkip, isNetblocksPageLoading]);

  const handleLoadMoreNetblocks = useCallback(() => {
    setIsLoadingMoreNetblocks(true);
    setNetblockSkip((currentSkip) => currentSkip + EXTERNAL_SCOPE_PAGE_SIZE);
  }, [setNetblockSkip]);
  const applyNetblockScope = useCallback(
    async ({ netblockIds, scope }: PendingNetblockScope) => {
      try {
        await runInBatches({
          ids: netblockIds,
          run: (netblockId) => updateNetblockScope({ netblockId, scope }),
        });
        toasterApi.success({
          title:
            netblockIds.length === 1
              ? t('updateSuccess')
              : t('bulkUpdateSuccess', { count: netblockIds.length }),
        });
        resetNetblockPagination();
        setSelectedNetblockIds(new Set());
      } catch (error: unknown) {
        console.error(toError(error));
        toasterApi.error({
          title:
            netblockIds.length === 1 ? t('updateError') : t('bulkUpdateError'),
        });
      }
    },
    [updateNetblockScope, resetNetblockPagination, t],
  );
  const confirmNetblockScopeIfNeeded = useCallback(
    ({ netblockIds, scope }: PendingNetblockScope) => {
      const shouldWarn =
        !isConnectorSurface &&
        scope === 'IN_SCOPE' &&
        netblockIds.some((netblockId) => {
          const netblock = displayedNetblocks.find(
            (item) => item.id === netblockId,
          );

          return needsCloudHostingWarning({
            cloudProvider: netblock?.cloudProvider,
            inventoryLinked: netblock?.inventoryLinked,
            cloudAllocationId: netblock?.cloudAllocationId,
            deprovisionedAt: netblock?.deprovisionedAt,
          });
        });

      if (shouldWarn) {
        setPendingHostingConfirm({ netblockIds, scope });

        return;
      }

      void applyNetblockScope({ netblockIds, scope });
    },
    [applyNetblockScope, displayedNetblocks, isConnectorSurface],
  );
  const handleNetblockScopeChange = useCallback(
    ({ id, scope }: ScopeEntityChange) => {
      confirmNetblockScopeIfNeeded({ netblockIds: [id], scope });
    },
    [confirmNetblockScopeIfNeeded],
  );
  const handleDomainScopeChange = useCallback(
    async ({ id, scope }: ScopeEntityChange) => {
      try {
        await updateDomainScope({ domainId: id, scope });
        toasterApi.success({ title: t('updateSuccess') });
      } catch (error: unknown) {
        console.error(toError(error));
        toasterApi.error({ title: t('updateError') });
      }
    },
    [updateDomainScope, t],
  );
  const handleBulkDomainScope = useCallback(
    async (scope: Scope) => {
      const ids = Array.from(selectedDomainIds);

      try {
        await runInBatches({
          ids,
          run: (domainId) => updateDomainScope({ domainId, scope }),
        });
        toasterApi.success({
          title: t('bulkUpdateSuccess', { count: ids.length }),
        });
        setSelectedDomainIds(new Set());
      } catch (error: unknown) {
        console.error(toError(error));
        toasterApi.error({ title: t('bulkUpdateError') });
      }
    },
    [selectedDomainIds, updateDomainScope, t],
  );
  const handleBulkScope = (scope: Scope) => {
    if (activeSection === 'netblocks') {
      confirmNetblockScopeIfNeeded({
        netblockIds: Array.from(selectedNetblockIds),
        scope,
      });

      return;
    }

    void handleBulkDomainScope(scope);
  };

  const toggleNetblockSelection = useCallback((id: string) => {
    setSelectedNetblockIds((previous) => {
      const next = new Set(previous);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);
  const handleDomainSelectionChange = useCallback((ids: string[]) => {
    setSelectedDomainIds(new Set(ids));
  }, []);
  const handleSectionChange = (section: ScopeSection) => {
    setActiveSection(section);
    resetNetblockPagination();
    setScopeFilter('all');
    setSelectedNetblockIds(new Set());
    setSelectedDomainIds(new Set());
    setIsAddDomainDialogOpen(false);
    setIsAddNetblockDialogOpen(false);
  };

  const handleFilterChange = (filter: ScopeFilter) => {
    resetNetblockPagination();
    setScopeFilter(filter);
    setSelectedNetblockIds(new Set());
    setSelectedDomainIds(new Set());
  };

  const handleAddClick = () => {
    if (activeSection === 'domains') {
      setIsAddNetblockDialogOpen(false);
      setIsAddDomainDialogOpen(true);

      return;
    }

    setIsAddDomainDialogOpen(false);
    setIsAddNetblockDialogOpen(true);
  };

  const addValues = async ({
    values,
    isPending,
    add,
    operation,
  }: {
    values: string[];
    isPending: boolean;
    add: (values: string[]) => Promise<unknown>;
    operation: ExternalScopeAddOperation;
  }) => {
    if (isPending) {
      return;
    }

    try {
      await add(values);
      resetNetblockPagination();
      toasterApi.success({ title: t('saveSuccess') });
    } catch (error: unknown) {
      onAddError?.({ error, operation });
      toasterApi.error({ title: t('saveError') });

      throw error;
    }
  };

  const handleHostingConfirm = () => {
    if (!pendingHostingConfirm) {
      return;
    }

    const pending = pendingHostingConfirm;

    setPendingHostingConfirm(null);
    void applyNetblockScope(pending);
  };

  return {
    isScanActive,
    isReadOnly,
    isDisabled,
    activeSection,
    onSectionChange: handleSectionChange,
    netblocksTotal: netblockCounts.all,
    domainsTotal: domainCounts.all,
    scopeFilter,
    scopeCounts,
    onFilterChange: handleFilterChange,
    selectedCount:
      activeSection === 'netblocks'
        ? selectedNetblockIds.size
        : selectedDomainIds.size,
    onBulkApprove: () => handleBulkScope('IN_SCOPE'),
    onBulkReject: () => handleBulkScope('OUT_OF_SCOPE'),
    netblocks: displayedNetblocks,
    isNetblocksLoading: isNetblocksPageLoading && netblockSkip === 0,
    selectedNetblockIds,
    onToggleNetblockSelection: toggleNetblockSelection,
    onNetblockScopeChange: handleNetblockScopeChange,
    hasMoreNetblocks,
    canLoadMoreNetblocks:
      activeSection === 'netblocks' &&
      hasMoreNetblocks &&
      !isNetblocksPageLoading &&
      !isLoadingMoreNetblocks,
    isLoadingMoreNetblocks,
    onLoadMoreNetblocks: handleLoadMoreNetblocks,
    domains,
    domainTreeTotal,
    isDomainsLoading,
    selectedDomainIds: [...selectedDomainIds],
    onDomainSelectionChange: handleDomainSelectionChange,
    onDomainScopeChange: handleDomainScopeChange,
    isAddPending:
      activeSection === 'domains' ? isDomainAddPending : isNetblockAddPending,
    onAddClick: handleAddClick,
    isAddDomainDialogOpen,
    onAddDomainDialogOpenChange: setIsAddDomainDialogOpen,
    isAddNetblockDialogOpen,
    onAddNetblockDialogOpenChange: setIsAddNetblockDialogOpen,
    onAddDomains: (values) =>
      addValues({
        values,
        isPending: isDomainAddPending,
        add: addDomains,
        operation: 'addSurfaceDomains',
      }),
    onAddNetblocks: (values) =>
      addValues({
        values,
        isPending: isNetblockAddPending,
        add: addNetblocks,
        operation: 'addSurfaceNetblocks',
      }),
    isDomainAddPending,
    isNetblockAddPending,
    existingDomainValues,
    existingNetblockValues,
    netblockValidator: isConnectorSurface
      ? validateInternalNetblock
      : validateNetblock,
    isHostingConfirmOpen: pendingHostingConfirm !== null,
    onHostingConfirmCancel: () => setPendingHostingConfirm(null),
    onHostingConfirm: handleHostingConfirm,
  };
}
